/**
 * MemoryBlock
 *
 * Represents a block of memory within a buffer.
 * Each MemoryBlock tracks its size, offset within the buffer, free status,
 * and links to adjacent memory blocks for efficient memory management.
 * Used for implementing custom memory allocation strategies.
 */
export class MemoryBlock {

  /**
   * Creates an instance of MemoryBlock.
   *
   * @param {number} size - The size in bytes of the memory block.
   * @param {number} offset - The offset in bytes of the memory block within the buffer.
   * @param {boolean} isFree - Indicates if the block is free or allocated.
   */
  constructor(size, offset = 0, isFree = true) {
    /**
     * The size in bytes of the memory block.
     * @type {number}
     */
    this.size = size;

    /**
     * The offset in bytes of the memory block within the buffer.
     * @type {number}
     */
    this.offset = offset;

    /**
     * The next memory block in the linked list.
     * @type {MemoryBlock|null}
     */
    this.next = null;

    /**
     * The previous memory block in the linked list.
     * @type {MemoryBlock|null}
     */
    this.prev = null;

    /**
     * Indicates if the block is free or allocated.
     * @type {boolean}
     */
    this.isFree = isFree;

    /**
     * The buffer that contains this memory block.
     * @type {WebGLBuffer|GPUBuffer|null}
     */
    this.buffer = null;

    /**
     * The free list that contains this memory block.
     * @type {FreeList|null}
     */
    this.freeList = null;

    // Seal this object to prevent further modifications
    Object.seal(this);
  }
}